<template>
  <div
    class="tw-flex tw-flex-col tw-gap-2"
    data-testid="user-panel-access-picker"
  >
    <SfSearchInput
      :model-value="query"
      :placeholder="placeholder"
      :aria-label="placeholder"
      :show-result-navigation="false"
      :focused-at="1"
      wide
      autocomplete="off"
      @update:model-value="runSearch"
      @clear="runSearch('')"
    />

    <div
      class="
        tw-max-h-60
        tw-overflow-y-auto
        tw-rounded
        tw-border
        tw-border-solid
        tw-border-sf-slate-lighter-1
        tw-bg-sf-slate-white
      "
      aria-live="polite"
    >
      <p
        v-if="searching"
        class="tw-m-0 tw-p-4 tw-text-sm tw-text-sf-slate-light-2"
      >
        {{ I18n.t('common.loading') }}
      </p>
      <p v-else-if="failed" class="tw-m-0 tw-p-4 tw-text-sm tw-text-sf-danger">
        {{ I18n.t('user.panel.search_error') }}
      </p>
      <p
        v-else-if="!options.length"
        class="tw-m-0 tw-p-4 tw-text-sm tw-text-sf-slate-light-2"
      >
        {{ I18n.t('user.panel.no_search_results') }}
      </p>
      <ul v-else class="tw-m-0 tw-list-none tw-p-0">
        <li v-for="option in options" :key="option.id">
          <button
            type="button"
            class="
              tw-w-full
              tw-cursor-pointer
              tw-truncate
              tw-border-0
              tw-bg-transparent
              tw-px-6
              tw-py-2
              tw-text-left
              tw-text-sm
              tw-text-sf-slate-dark-2
              hover:tw-bg-sf-slate-subtle-1
              focus-visible:tw-bg-sf-slate-subtle-1
            "
            @click="emit('select', option)"
          >
            {{ option.name }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AutocompleteOption } from '@app/application/user-management/types'

import { onBeforeUnmount, onMounted, ref } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'
import SfSearchInput from '@app/shared/vue/components/sf-search/search-input.vue'

defineOptions({ name: 'AccessPicker' })

const props =
  defineProps<{
    placeholder: string
    search: (
      query: string,
      signal: AbortSignal
    ) => Promise<AutocompleteOption[] | null>
  }>()

const emit = defineEmits<{ select: [option: AutocompleteOption] }>()

const I18n = useI18n()

const query = ref('')
const options = ref<AutocompleteOption[]>([])
const searching = ref(false)
const failed = ref(false)

// One controller per picker: the panel can show two pickers at once, and a
// shared key would cancel the other one's search.
let controller: AbortController | null = null

onMounted(() => runSearch(''))
onBeforeUnmount(() => controller?.abort())

async function runSearch(value: string | null): Promise<void> {
  controller?.abort()
  controller = new AbortController()

  const { signal } = controller

  query.value = value ?? ''
  failed.value = false
  searching.value = true

  try {
    const results = await props.search(query.value, signal)

    if (signal.aborted) return

    options.value = results ?? []
  } catch {
    if (signal.aborted) return

    options.value = []
    failed.value = true
  }

  searching.value = false
}
</script>
